/**
 * Displacement flow layer renderer.
 * ArcLayer with arrows colored by displacement scale (number of people).
 * Small flows are thin/cool, large flows are thick/warm.
 * Also renders destination markers for arrival points.
 */

import { ArcLayer, ScatterplotLayer } from '@deck.gl/layers';
import type { Layer } from '@deck.gl/core';
import type { NormalizedEvent } from '@/sources/base-source';

/** Layer IDs handled by this renderer. */
export const DISPLACEMENT_LAYER_IDS = new Set(['displacement']);

/** Arc data extracted from displacement events. */
export interface DisplacementArc {
  event: NormalizedEvent;
  source: [number, number];
  target: [number, number];
  scale: number;
}

/**
 * Displacement flow colors — WM's directional blue -> green gradient
 * (DeckGLMap.ts:6480-6481, dark theme; Sentinel's map is dark-first).
 */
export const FLOW_SOURCE_COLOR: [number, number, number, number] = [100, 150, 255, 180]; // blue
export const FLOW_TARGET_COLOR: [number, number, number, number] = [100, 255, 200, 180]; // green

/** Extract displacement arc data from events. Shared with the globe bridge. */
export function extractDisplacementArcs(events: NormalizedEvent[]): DisplacementArc[] {
  const arcs: DisplacementArc[] = [];

  for (const event of events) {
    const raw = event.raw as Record<string, unknown> | undefined;
    if (!raw) continue;

    const src = raw.sourceCoord as [number, number] | undefined;
    const tgt = raw.targetCoord as [number, number] | undefined;
    const scale = typeof raw.displacedCount === 'number'
      ? raw.displacedCount
      : typeof raw.scale === 'number'
        ? raw.scale
        : 1;

    if (src && tgt && Array.isArray(src) && Array.isArray(tgt)) {
      arcs.push({ event, source: src, target: tgt, scale });
    }
  }

  return arcs;
}

/** Create deck.gl layers for displacement flow data. */
export function createDisplacementLayers(
  events: NormalizedEvent[],
  zoom: number,
): Layer[] {
  // Top 50 flows, width ranked against the visible set so the full 1-8px
  // range is always used (WM DeckGLMap.ts:6472-6484)
  const arcs = extractDisplacementArcs(events)
    .sort((a, b) => b.scale - a.scale)
    .slice(0, 50);
  const opacity = Math.min(0.9, 0.2 + zoom * 0.08);
  const layers: Layer[] = [];

  if (arcs.length > 0) {
    const maxCount = Math.max(1, ...arcs.map((a) => a.scale));

    layers.push(new ArcLayer<DisplacementArc>({
      id: 'displacement-arcs',
      data: arcs,
      pickable: true,
      getSourcePosition: (d) => d.source,
      getTargetPosition: (d) => d.target,
      getSourceColor: FLOW_SOURCE_COLOR,
      getTargetColor: FLOW_TARGET_COLOR,
      getWidth: (d) => Math.max(1, (d.scale / maxCount) * 8),
      widthMinPixels: 1,
      widthMaxPixels: 8,
      updateTriggers: {
        getWidth: [maxCount, arcs.length],
      },
    }));

    // Destination markers (arrival points) — Sentinel addition, kept
    layers.push(new ScatterplotLayer<DisplacementArc>({
      id: 'displacement-destinations',
      data: arcs,
      pickable: false,
      filled: true,
      radiusUnits: 'pixels' as const,
      getPosition: (d) => d.target,
      getFillColor: [FLOW_TARGET_COLOR[0], FLOW_TARGET_COLOR[1], FLOW_TARGET_COLOR[2]],
      getRadius: 4,
      opacity: opacity * 0.7,
    }));
  }

  // Single-point displacement events (no arc data) rendered as dots
  const pointEvents = events.filter((e) => {
    const raw = e.raw as Record<string, unknown> | undefined;
    return e.location && !(raw?.sourceCoord && raw?.targetCoord);
  });

  if (pointEvents.length > 0) {
    layers.push(new ScatterplotLayer<NormalizedEvent>({
      id: 'displacement-points',
      data: pointEvents,
      pickable: true,
      filled: true,
      radiusUnits: 'pixels' as const,
      getPosition: (d) => d.location ? [d.location.lng, d.location.lat] : [0, 0],
      getFillColor: [255, 102, 0],
      getRadius: 5,
      opacity,
    }));
  }

  return layers;
}
